import AddIcon from "@mui/icons-material/Add";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { useCallback, useState } from "react";
import SavedCardChip from "@/components/campaignBuilder/review/SavedCardChip/SavedCardChip";
import AddPaymentMethodModal from "@/components/usersOrgs/AddPaymentMethodModal";
import { useCampaignPaymentMethod } from "@/hooks/useCampaignPaymentMethod";
import { useAwaitAddedPaymentMethod, usePaymentMethods } from "@/hooks/usePaymentMethods";

/**
 * The buy box's payment-method block, which MOVES depending on whether a card
 * is on file. Mirrors the prototype's `renderPaymentMethodUI()`
 * (Campaign-Builder-Master-Prototype-Stage2-Revisions.html ~line 23750), which
 * toggles two separate DOM nodes:
 *
 * - `#topPaymentSection` + `#topPaymentDivider` (markup ~line 10957) — the
 *   saved-card chip at the TOP of the buy box, shown only once a card exists;
 * - `#checkoutPaymentEmpty` (`.checkout-add-payment-row`, markup ~line 11060) —
 *   the "Add Payment Method" button just above the CTA, shown only while none
 *   does.
 *
 * The port keeps that as one component rendered twice, once per `placement`,
 * each returning null in the state it doesn't serve — so the swap is decided
 * here rather than duplicated in the card. `top` also renders its own trailing
 * divider, matching `#topPaymentDivider`.
 *
 * Unlike the prototype's mock card capture, this reads the group's real saved
 * cards and adds one through the existing SetupIntent + Stripe Payment Element
 * modal, so no card data passes through this app.
 */
export interface PaymentMethodSectionProps {
  /** `"top"` renders the saved chip, `"bottom"` the add button — never both. */
  placement: "top" | "bottom";
  /**
   * The wallet this campaign is funded from, when the caller chose one (Pro Plus or Enterprise).
   *
   * The card must follow the SAME group as the credits, or the chip shows one group's card while
   * the checkout bills another's — and a card added from here would land on the wrong group.
   */
  walletGroupId?: number | null;
  /**
   * The card this campaign is nominated against. `undefined` means "not chosen", which falls back
   * to the group's default — so the nomination can stay unset until the buyer actually picks.
   */
  value?: number | null;
  /** Called when the buyer picks a different saved card. Campaign-local: the group's own default
   *  is NOT rewritten, because changing a group's billing from inside one checkout is a side
   *  effect nobody asked for. */
  onSelect?: (paymentMethodId: number) => void;
  className?: string;
}

/** Sentinel for the picker's last item, which opens the add-a-card modal instead of selecting. */
const ADD_NEW = "add";

export default function PaymentMethodSection({
  placement,
  walletGroupId,
  value,
  onSelect,
  className,
}: PaymentMethodSectionProps) {
  const {
    card: defaultCard,
    groupId,
    canAddCard,
    isLoading,
  } = useCampaignPaymentMethod(walletGroupId);
  const { data: cards } = usePaymentMethods(groupId);
  const handleAdded = useAwaitAddedPaymentMethod(groupId);
  const [addOpen, setAddOpen] = useState(false);
  const [picking, setPicking] = useState(false);

  const list = cards ?? [];
  // The nomination wins; the group's default is only where it starts.
  const card = list.find((c) => c.id === value) ?? defaultCard;

  const handleOpenPaymentModal = useCallback(() => {
    if (groupId == null) return;
    setAddOpen(true);
  }, [groupId]);

  const handlePick = useCallback(
    (next: string) => {
      if (next === ADD_NEW) {
        handleOpenPaymentModal();
        return;
      }
      onSelect?.(Number(next));
      setPicking(false);
    },
    [handleOpenPaymentModal, onSelect],
  );

  const handleClosePaymentModal = useCallback(() => setAddOpen(false), []);

  // `handleAdded` polls until the `setup_intent.succeeded` webhook writes the row — see
  // `useAwaitAddedPaymentMethod`. Locally that webhook only arrives with `stripe listen`
  // forwarding to the API; without it no amount of polling will surface the card.
  const canAdd = canAddCard && groupId != null;

  // No card data ever reaches this app: the shared group-billing modal collects it with Stripe's
  // Payment Element and confirms the SetupIntent straight against Stripe.
  const addModal = groupId != null && (
    <AddPaymentMethodModal
      open={addOpen}
      groupId={groupId}
      onClose={handleClosePaymentModal}
      onAdded={handleAdded}
    />
  );

  if (placement === "top") {
    if (isLoading || !card) return null;
    return (
      <div className={className}>
        <div className="mb-3 font-lato text-sm font-bold text-rodeo-black">Payment Method</div>
        {picking ? (
          // Every saved card, plus adding one. "Change" used to go straight to the SetupIntent
          // modal, which meant it could only ever ADD — a group with three cards on file had no
          // way to bill a different one.
          <TextField
            select
            fullWidth
            size="small"
            label="Payment method"
            value={card ? String(card.id) : ""}
            onChange={(e) => handlePick(e.target.value)}
          >
            {list.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {(c.brand ?? "Card").toUpperCase()} •••• {c.last4 ?? "••••"}
              </MenuItem>
            ))}
            {canAdd && (
              <MenuItem value={ADD_NEW}>
                <AddIcon sx={{ fontSize: 16, mr: 1 }} aria-hidden="true" />
                Add a new card…
              </MenuItem>
            )}
          </TextField>
        ) : (
          <SavedCardChip
            card={card}
            onChange={() => setPicking(true)}
            // With one card and no permission to add another there is nothing to change TO, so the
            // action would open a picker of a single option.
            canChange={list.length > 1 || canAdd}
          />
        )}
        <div className="my-3.5 h-px bg-card-border" />
        {addModal}
      </div>
    );
  }

  if (card) return null;

  return (
    <div className={["mt-4", className].filter(Boolean).join(" ")}>
      {isLoading ? (
        <div
          className="h-12 animate-pulse rounded-btn bg-wax"
          role="status"
          aria-label="Loading payment methods"
        />
      ) : canAdd ? (
        <button
          type="button"
          onClick={handleOpenPaymentModal}
          className="inline-flex w-full cursor-pointer items-center justify-center gap-2.5 rounded-btn bg-rodeo-black px-5 py-3.5 font-lato text-[13px] font-bold tracking-wide text-white uppercase transition-colors hover:bg-background-dark"
        >
          <AddIcon sx={{ fontSize: 18 }} aria-hidden="true" />
          Add Payment Method
        </button>
      ) : (
        <p className="font-lato text-[13px] text-graphite">
          No payment method on file. Ask a group admin to add one before launching this campaign.
        </p>
      )}
      {addModal}
    </div>
  );
}
